import React from "react";
import { useFormContext } from "react-hook-form";
import { Validation } from "../../types/form";
import { FieldErrorMessage } from "../ui/Text";
import { handleKeyDown } from "@/utils/helper/input";

interface NumberInputValidation extends Validation {
  min?: number;
  max?: number;
  allowDecimals?: boolean;
  decimalPlaces?: number;
  allowNegative?: boolean;
}

interface NumberInputProps {
  label: string;
  field: string;
  unit?: string;
  placeholder: string;
  disabled?: boolean;
  validation: NumberInputValidation;
  defaultValue?: string;
}

const NumberInput: React.FC<NumberInputProps> = ({ label, field, disabled = false, validation, placeholder, defaultValue, unit }) => {
  const {
    register,
    formState: { errors },
  } = useFormContext();

  const errorMessage: any = errors[field]?.message;

  const validateNumber = (value: string): boolean | string => {
    if (!value && !validation?.required) return true;
    const numberValue = Number(value);

    if (isNaN(numberValue)) {
      return "Please enter a valid number";
    }
    if (validation?.min && numberValue < validation.min) {
      return `Value must be greater than or equal to ${validation.min}`;
    }
    if (validation?.max && numberValue > validation.max) {
      return `Value must be less than or equal to ${validation.max}`;
    }
    if (!validation?.allowDecimals && !Number.isInteger(numberValue)) {
      return "Decimals are not allowed";
    }

    if (validation?.decimalPlaces) {
      const decimalParts = value.split(".");
      if (decimalParts[1] && decimalParts[1].length > validation.decimalPlaces) {
        return `Maximum ${validation.decimalPlaces} decimal places allowed`;
      }
    }
    return true;
  };

  const getRegisterOptions = () => {
    // If field is disabled
    if (disabled) {
      return {};
    }
    const registerOptions: any = {
      validate: validateNumber,
    };
    if (validation?.required) {
      registerOptions.required = "This field is required.";
    }
    if (validation?.maxLength) {
      registerOptions.maxLength = {
        value: validation.maxLength,
        message: `The maximum digit allowed is ${validation.maxLength}.`,
      };
    }
    if (validation?.minLength) {
      registerOptions.minLength = {
        value: validation.minLength,
        message: `The minimum digit allowed is ${validation.minLength}.`,
      };
    }
    if (validation?.pattern) {
      registerOptions.validate = {
        pattern: (value: string) => {
          const patternValue = validation?.pattern?.value;
          if (!patternValue) throw Error("Invalid Pattern");
          const regex = new RegExp(patternValue);
          if (regex.test(value)) {
            return true;
          } else {
            return validation.pattern?.message || "Invalid format";
          }
        },
      };
    }

    return registerOptions;
  };

  return (
    <div className="relative">
      <input
        type="text"
        inputMode="numeric"
        id={label + "_number"}
        placeholder={placeholder}
        className={`w-full px-3 py-7 text-text-default text-13 rounded-lg outline-none border border-fieldBorder hover:border-gray-400 focus:border-text-primary-500 ${
          errors[field]?.message ? "border-text-error" : ""
        } ${disabled ? "cursor-not-allowed" : "cursor-text"}`}
        {...register(field, getRegisterOptions())}
        disabled={disabled}
        onKeyDown={(e) => handleKeyDown(validation, e)}
        defaultValue={defaultValue}
      />
      {unit && <span className="absolute top-1/2 -translate-y-1/2 right-2 !leading-normal text-10 text-text-gray">{unit}</span>}{" "}
      {errorMessage && <FieldErrorMessage text={errorMessage} />}
    </div>
  );
};

export default NumberInput;
